import { bcs } from "@mysten/sui/bcs";

import { RPC_URLS } from "@/constants/chain-rpc";
import { getSolanaConfig, readDexSdk } from "@/contexts/dex-sdk";

import { CCTPTransientError } from "../errors";
import { suiGraphql } from "../v2/burn-sui";
import {
  appCctpNetwork,
  APTOS_MESSAGE_TRANSMITTER_V2,
  type CctpNetwork,
  EVM_MESSAGE_TRANSMITTER_V2,
  SOLANA_MESSAGE_TRANSMITTER_V2,
} from "../v2/contracts";
import { SUI_CCTP_V2 } from "../v2/contracts-sui";
import { APTOS_CCTP_DOMAIN_ID, SOLANA_CCTP_DOMAIN_ID, SUI_CCTP_DOMAIN_ID } from "./contracts";
import { base58Decode, base58Encode, findProgramAddress } from "./solana";

/** `unknown`: no v2 deployment on the destination, or Circle hasn't assigned the nonce yet. */
export type ClaimStatus = "claimed" | "unclaimed" | "unknown";

const EVM_RPC_BY_DOMAIN: Record<CctpNetwork, Record<number, string>> = {
  mainnet: { 0: RPC_URLS.Ethereum, 3: RPC_URLS.Arbitrum, 6: RPC_URLS.Base },
  testnet: { 0: RPC_URLS.Sepolia, 3: RPC_URLS.ArbitrumSepolia, 6: RPC_URLS.BaseSepolia },
};

const USED_NONCES_SELECTOR = "0xfeb61724"; // usedNonces(bytes32)
const NONCE_RE = /^0x[0-9a-fA-F]{64}$/;
const ZERO_NONCE = `0x${"0".repeat(64)}`;
// Anchor account layout: 8-byte discriminator, then `is_used: bool`.
const USED_NONCE_FLAG_OFFSET = 8;

/** The Solana account MessageTransmitterV2 creates when it consumes `nonce`. */
export function getV2UsedNonceAddress(nonce: string): string {
  const seeds = [
    new TextEncoder().encode("used_nonce"),
    Uint8Array.from(nonce.slice(2).match(/../g) ?? [], (byte) => parseInt(byte, 16)),
  ];
  return base58Encode(findProgramAddress(seeds, base58Decode(SOLANA_MESSAGE_TRANSMITTER_V2)));
}

/**
 * Whether a CCTP v2 message has been claimed on its destination chain. v2 keys every check on
 * the 32-byte nonce Circle's attesters assign, so it needs the attested message, not the one
 * emitted on the source chain (whose nonce is all zeros).
 *
 * Throws `CCTPTransientError` on RPC failure, so an outage is never read as "unclaimed".
 */
export async function checkV2NonceUsed(
  nonce: string,
  destDomain: number,
  network: CctpNetwork = appCctpNetwork(),
): Promise<ClaimStatus> {
  if (!NONCE_RE.test(nonce)) throw new Error(`Invalid CCTP v2 nonce: ${nonce}`);
  if (nonce.toLowerCase() === ZERO_NONCE) return "unknown";

  let used: boolean;
  if (destDomain === APTOS_CCTP_DOMAIN_ID) {
    used = await checkOnAptos(nonce, network);
  } else if (destDomain === SOLANA_CCTP_DOMAIN_ID) {
    used = await checkOnSolana(nonce);
  } else if (destDomain === SUI_CCTP_DOMAIN_ID) {
    used = await checkOnSui(nonce, network);
  } else {
    const rpc = EVM_RPC_BY_DOMAIN[network][destDomain];
    if (!rpc) return "unknown";
    used = await checkOnEvm(nonce, destDomain, rpc, EVM_MESSAGE_TRANSMITTER_V2[network]);
  }
  return used ? "claimed" : "unclaimed";
}

async function checkOnAptos(nonce: string, network: CctpNetwork): Promise<boolean> {
  try {
    const [isUsed] = await readDexSdk.deps.aptos.view<[boolean]>({
      payload: {
        function: `${APTOS_MESSAGE_TRANSMITTER_V2[network]}::message_transmitter::is_nonce_used`,
        typeArguments: [],
        functionArguments: [BigInt(nonce).toString()],
      },
    });
    return isUsed;
  } catch (err) {
    throw new CCTPTransientError(`Aptos v2 nonce check failed (nonce=${nonce})`, err);
  }
}

async function checkOnEvm(
  nonce: string,
  destDomain: number,
  rpc: string,
  contract: string,
): Promise<boolean> {
  const result = await jsonRpc<string>(
    rpc,
    "eth_call",
    [{ to: contract, data: `${USED_NONCES_SELECTOR}${nonce.slice(2)}` }, "latest"],
    `EVM v2 nonce check (destDomain=${destDomain}, nonce=${nonce})`,
  );
  return BigInt(result) > BigInt(0);
}

async function checkOnSolana(nonce: string): Promise<boolean> {
  const result = await jsonRpc<{ value: { data?: [string, string] } | null }>(
    getSolanaConfig().rpc,
    "getAccountInfo",
    [getV2UsedNonceAddress(nonce), { encoding: "base64" }],
    `Solana v2 nonce check (nonce=${nonce})`,
  );
  // The account only exists once the nonce has been consumed.
  const encoded = result.value?.data?.[0];
  if (!encoded) return false;
  const raw = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
  return raw[USED_NONCE_FLAG_OFFSET] === 1;
}

const SUI_USED_NONCE_QUERY = `query($table: SuiAddress!, $key: Base64!) {
  address(address: $table) { dynamicField(name: { type: "u256", bcs: $key }) { value { ... on MoveValue { json } } } }
}`;

/** Circle's Sui message transmitter keeps consumed nonces in a `Table<u256, bool>`. */
async function checkOnSui(nonce: string, network: CctpNetwork): Promise<boolean> {
  interface Data {
    address: { dynamicField: { value: { json: unknown } } | null } | null;
  }
  let data: Data;
  try {
    data = await suiGraphql<Data>(network, SUI_USED_NONCE_QUERY, {
      table: SUI_CCTP_V2[network].usedNoncesTable,
      key: bcs.u256().serialize(BigInt(nonce)).toBase64(),
    });
  } catch (err) {
    throw new CCTPTransientError(`Sui v2 nonce check failed (nonce=${nonce})`, err);
  }
  return data.address?.dynamicField?.value.json === true;
}

async function jsonRpc<T>(
  url: string,
  method: string,
  params: unknown[],
  label: string,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
    });
  } catch (err) {
    throw new CCTPTransientError(`${label} request failed`, err);
  }
  if (!response.ok) throw new CCTPTransientError(`${label} returned ${response.status}`);

  let data: { result?: T; error?: unknown };
  try {
    data = (await response.json()) as typeof data;
  } catch (err) {
    throw new CCTPTransientError(`${label} returned invalid JSON`, err);
  }
  // Unlike v1, an RPC error isn't read as "unsupported": every destination here has a v2 deployment.
  if (data.error !== undefined || data.result === undefined) {
    throw new CCTPTransientError(`${label} returned an RPC error`, data.error);
  }
  return data.result;
}
